import AddIcon from "@mui/icons-material/Add";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Grid,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import { uniq } from "lodash";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import {
  getAllServiceTableReportData,
  getServiceTableReportData,
} from "../../../../redux/reducers/reportSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import { ServiceTypes } from "../../../../util/dropList";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";
import "./ServiceAnalytics.css";

const tableHdrs = [
  "serviceName",
  "timesUsed",
  "totalRevenue",
  "totalDiscount",
  "totalRefund",
  "averageTimeSpent",
  "type",
  "category",
  "subcategory",
  "sacCode",
  "variantName",
  "variantNumber",
  "sellingPrice",
];
const filterByList = [
  "No. of Times Used",
  "Total Revenue",
  "Total Discount",
  "Total Refund",
  "Avg. Time Spent",
  "Type",
  "SAC Code",
  "Variant ID",
  "Selling Price",
];
const dropFields = ["Type"];
const initSearch = {
  serviceName: "",
  category: [],
  subcategory: [],
  variantName: "",
};
const initFilter = {
  visible: false,
  added: false,
  selectedList: [],
  values: {},
};

const ServiceTableReport = () => {
  const dispatch = useDispatch();
  const hasFetchedAllRef = useRef(false);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("all");
  const [selectedPeriod, setSelectedPeriod] = useState("week");
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs?.slice(0, 5));
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.slice(0, 5)?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [filterValues, setFilterValues] = useState(initFilter);
  const { serviceTable, allServiceTable } = useSelector(
    (state) => state?.report,
  );
  const rowsPerPage = 10;

  useEffect(() => {
    onApiCall(1).then(() => {
      if (hasFetchedAllRef.current) return;
      hasFetchedAllRef.current = true;
      dispatch(getAllServiceTableReportData());
    });
  }, [
    selectedCategoryFilter,
    selectedPeriod,
    searchValues?.serviceName,
    searchValues?.category,
    searchValues?.subcategory,
    searchValues?.variantName,
  ]);

  const overviewData = serviceTable?.overview || serviceTable?.data || {};
  const cards = overviewData?.cards || {};

  const summaryCards = [
    {
      title: "Medical services",
      value:
        cards?.medicalServicesDisplay ??
        (cards?.medicalServices !== undefined
          ? `${Number(cards.medicalServices).toLocaleString()}`
          : "1,218"),
    },
    {
      title: "Non-medical services",
      value:
        cards?.nonMedicalServicesDisplay ??
        (cards?.nonMedicalServices !== undefined
          ? `${Number(cards.nonMedicalServices).toLocaleString()}`
          : "0"),
    },
  ];

  // Chart 1: Top 5 services by revenue (Distributed Bar Chart)
  const rawTopServices = Array.isArray(overviewData?.top5ServicesByRevenue?.top5Services)
    ? overviewData.top5ServicesByRevenue.top5Services
    : [];

  const topServicesNames =
    rawTopServices.length > 0
      ? rawTopServices.map((item) => item?.serviceName || "Service")
      : [
          "New Serv 10",
          "Cryptorchid Neutering - M",
          "Cryptorchid Neutering - S",
          "New Serv 8",
          "New Serv 6",
        ];

  const topServicesValues =
    rawTopServices.length > 0
      ? rawTopServices.map((item) =>
          typeof item?.currentPeriodRevenue === "number" && !isNaN(item.currentPeriodRevenue)
            ? item.currentPeriodRevenue
            : typeof item?.serviceRevenue === "number" && !isNaN(item.serviceRevenue)
            ? item.serviceRevenue
            : 0
        )
      : [110000, 40000, 25000, 18000, 15000];

  const topServicesChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: false, columnWidth: "40%", borderRadius: 3, distributed: true } },
    colors: ["#2563EB", "#10B981", "#F59E0B", "#F97316", "#8B5CF6"],
    xaxis: {
      categories: topServicesNames,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "11px", colors: "#616161" } },
    },
    yaxis: {
      min: 0,
      labels: {
        formatter: (val) => {
          if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
          if (val >= 1000) return `${(val / 1000).toFixed(1)}k`;
          return `${val}`;
        },
        style: { fontFamily: "Montserrat, sans-serif", fontSize: "12px", colors: "#616161" },
      },
    },
    legend: { show: false },
    grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
  };

  const topServicesChartSeries = [
    { name: "Revenue", data: topServicesValues },
  ];

  // Chart 2: Frequently used — medical (Horizontal Bar Chart)
  const rawFreqMedical = Array.isArray(overviewData?.frequentlyUsedMedical?.top5Services)
    ? overviewData.frequentlyUsedMedical.top5Services
    : [];

  const freqMedicalNames =
    rawFreqMedical.length > 0
      ? rawFreqMedical.map((item) => item?.serviceName || "Service")
      : [
          "New Serv",
          "Abdominal Cryptorchid ...",
          "Acetylcholine Receptor An...",
          "new serv 10",
          "Abdominal Cryptorchid Ne...",
        ];

  const freqMedicalValues =
    rawFreqMedical.length > 0
      ? rawFreqMedical.map((item) =>
          typeof item?.currentCount === "number" && !isNaN(item.currentCount)
            ? item.currentCount
            : typeof item?.currentRevenue === "number" && !isNaN(item.currentRevenue)
            ? item.currentRevenue
            : 0
        )
      : [20000, 22000, 48000, 105000, 125000];

  const freqMedicalChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, barHeight: "45%", borderRadius: 3 } },
    colors: ["#2563EB"],
    xaxis: {
      min: 0,
      labels: {
        formatter: (val) => {
          if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
          if (val >= 1000) return `${(val / 1000).toFixed(1)}k`;
          return `${val}`;
        },
        style: { fontFamily: "Montserrat, sans-serif", fontSize: "11px", colors: "#616161" },
      },
    },
    yaxis: {
      categories: freqMedicalNames,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "11px", colors: "#616161" } },
    },
    grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
  };

  const freqMedicalChartSeries = [
    { name: "Usage count", data: freqMedicalValues },
  ];

  // Chart 3: Frequently used — non-medical (Horizontal Bar Chart)
  const rawFreqNonMedical = Array.isArray(overviewData?.frequentlyUsedNonMedical?.top5Services)
    ? overviewData.frequentlyUsedNonMedical.top5Services
    : [];

  const freqNonMedicalNames =
    rawFreqNonMedical.length > 0
      ? rawFreqNonMedical.map((item) => item?.serviceName || "Service")
      : [];

  const freqNonMedicalValues =
    rawFreqNonMedical.length > 0
      ? rawFreqNonMedical.map((item) =>
          typeof item?.currentCount === "number" && !isNaN(item.currentCount)
            ? item.currentCount
            : typeof item?.currentRevenue === "number" && !isNaN(item.currentRevenue)
            ? item.currentRevenue
            : 0
        )
      : [];

  const freqNonMedicalChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, barHeight: "45%", borderRadius: 3 } },
    colors: ["#10B981"],
    xaxis: {
      min: 0,
      labels: {
        formatter: (val) => {
          if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
          if (val >= 1000) return `${(val / 1000).toFixed(1)}k`;
          return `${val}`;
        },
        style: { fontFamily: "Montserrat, sans-serif", fontSize: "11px", colors: "#616161" },
      },
    },
    yaxis: {
      categories: freqNonMedicalNames,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "11px", colors: "#616161" } },
    },
    grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
  };

  const freqNonMedicalChartSeries = [
    { name: "Usage count", data: freqNonMedicalValues },
  ];

  const handleExportExcel = () => {
    const tableData = serviceTable?.data ?? [];
    const exportData = tableData.map((row) => ({
      "Service Name": row?.serviceName ?? "",
      "Times Used": row?.timesUsed ?? 0,
      "Total Revenue": row?.totalRevenue ?? 0,
      "Total Discount": row?.totalDiscount ?? 0,
      "Total Refund": row?.totalRefund ?? 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Service Analytics");
    XLSX.writeFile(workbook, `Service_Analytics_Report_${moment().format("YYYYMMDD")}.xlsx`);
  };

  const handleExportPDF = () => {
    window.print();
  };

  const getFilterObj = (currentPage = 1, reset = false) => {
    setPage(currentPage);
    const reqObj = {
      type: "summary",
      page: currentPage,
      itemSize: rowsPerPage,
    };
    if (reset) return reqObj;
    if (selectedCategoryFilter !== "all") reqObj.category = selectedCategoryFilter;
    if (selectedPeriod) reqObj.period = selectedPeriod;
    if (searchValues?.serviceName?.length > 0) reqObj.serviceName = [searchValues?.serviceName];
    if (searchValues?.category?.length > 0) reqObj.category = searchValues?.category?.map((li) => li?.value);
    if (searchValues?.subcategory?.length > 0) reqObj.subcategory = searchValues?.subcategory?.map((li) => li?.value);
    if (searchValues?.variantName?.length > 0) reqObj.variantName = [searchValues?.variantName];
    return reqObj;
  };

  const onApiCall = (pge = 1, reset = false) => {
    return dispatch(getServiceTableReportData(getFilterObj(pge, reset)));
  };

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find((tblHdr) => tblHdr?.name === pd);
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => setEditColModVsble(false);

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders?.filter((act) => act?.isSelected)?.map((act) => act?.name);
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const totalPage = Math.ceil((serviceTable?.totalRecords ?? 0) / rowsPerPage);

  const handleChangePage = (e, selectedPage) => onApiCall(selectedPage);

  const getTableData = () => {
    return (serviceTable?.data ?? [])?.map((li) => ({
      ...li,
      serviceName: li?.serviceName ?? "",
      timesUsed: li?.timesUsed ?? 0,
      totalRevenue: Number(li?.totalRevenue ?? 0)?.toFixed(2),
      totalDiscount: Number(li?.totalDiscount ?? 0)?.toFixed(2),
      totalRefund: Number(li?.totalRefund ?? 0)?.toFixed(2),
    }));
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({ ...searchValues, [name?.trim()?.replace(/,$/, "")]: value });
  };

  const onFilterChange = (name, value) => setFilterValues({ ...filterValues, [name]: value });

  const onReset = () => {
    flushSync(() => setSearchValues(initSearch));
    flushSync(() => setFilterValues({ ...initFilter, selectedList: [], values: {} }));
    onApiCall(1, true);
  };

  const getServiceNameList = () => uniq((serviceTable?.data ?? [])?.map((li) => li?.serviceName)?.filter(Boolean));

  const getDropListByType = (typ) => {
    return typ === "Type" ? ServiceTypes : [];
  };

  const onApplyFilter = () => {
    onApiCall();
    setFilterValues({ ...filterValues, visible: false });
  };

  const onCloseFilter = () => setFilterValues({ ...filterValues, visible: false });

  const onFilterValuesChange = (e) => {
    const { name, value } = e?.target;
    setFilterValues({ ...filterValues, values: { ...filterValues?.values, [name?.trim()?.replace(/,$/, "")]: value } });
  };

  const onFilterAdd = (value) => setFilterValues({ ...filterValues, added: value });

  const onSelectFilter = (name) => {
    const reqObj = { ...filterValues };
    let reqList = filterValues?.selectedList;
    if (reqList?.includes(name)) reqList = reqList?.filter((item) => item !== name);
    else reqList.push(name);
    reqObj.selectedList = reqList;
    setFilterValues(reqObj);
  };

  const onFilterReset = () => setFilterValues({ ...initFilter, visible: true, selectedList: [], values: {} });

  return (
    <div className="service-analytics-container">
      {/* Title */}
      <h2 className="service-analytics-title">Service analytics</h2>


      {/* Top Filter Bar */}
      <div className="service-analytics-filter-bar">
        <Select
          size="small"
          value={selectedCategoryFilter}
          onChange={(e) => setSelectedCategoryFilter(e.target.value)}
          sx={{
            height: 36,
            fontSize: 13,
            fontFamily: "Montserrat, sans-serif",
            borderRadius: "6px",
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
          }}
        >
          <MenuItem value="all">All categories</MenuItem>
        </Select>

        <div className="service-analytics-period-group">
          {[
            { label: "Week", value: "week" },
            { label: "Month", value: "month" },
            { label: "Quarter", value: "quarter" },
            { label: "Year", value: "year" },
          ].map((item) => (
            <button
              key={item.value}
              type="button"
              className={`service-analytics-period-btn ${selectedPeriod === item.value ? "active" : ""
                }`}
              onClick={() => setSelectedPeriod(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Export Action Buttons */}
      <div className="service-analytics-export-group">
        <button type="button" className="service-analytics-export-btn" onClick={handleExportExcel}>
          <DownloadIcon sx={{ fontSize: 16 }} /> Export Excel
        </button>
        <button type="button" className="service-analytics-export-btn" onClick={handleExportPDF}>
          <DownloadIcon sx={{ fontSize: 16 }} /> Export PDF
        </button>
      </div>

      {/* Summary Cards Grid (2 Cards) */}
      <div className="service-analytics-cards-grid">
        {summaryCards.map((card, idx) => (
          <div className="service-analytics-card" key={idx}>
            <h4 className="service-analytics-card-title">{card.title}</h4>
            <div className="service-analytics-card-value-container">
              <h2 className="service-analytics-card-value">{card.value}</h2>
            </div>
          </div>
        ))}
      </div>

      {/* Full-width Bar Chart: Top 5 services by revenue */}
      <div className="service-analytics-chart-card">
        <div className="service-analytics-chart-header">
          Top 5 services by revenue
        </div>
        <Chart
          options={topServicesChartOptions}
          series={topServicesChartSeries}
          type="bar"
          height={280}
        />
      </div>

      {/* Side-by-side Grid: Frequently used medical + Frequently used non-medical */}
      <div className="service-analytics-charts-grid">
        <div className="service-analytics-chart-card">
          <div className="service-analytics-chart-header">
            Frequently used — medical
          </div>
          <Chart
            options={freqMedicalChartOptions}
            series={freqMedicalChartSeries}
            type="bar"
            height={280}
          />
        </div>

        <div className="service-analytics-chart-card">
          <div className="service-analytics-chart-header">
            Frequently used — non-medical
          </div>
          {rawFreqNonMedical.length > 0 ? (
            <Chart
              options={freqNonMedicalChartOptions}
              series={freqNonMedicalChartSeries}
              type="bar"
              height={280}
            />
          ) : (
            <Typography className="gray38 fs14 txt-regular p20">
              No non-medical services used in this period.
            </Typography>
          )}
        </div>
      </div>

      {/* Drill-down Section with Top Right Edit Columns */}
      <div className="flex-row-between-align-center mv10">
        <div className="service-analytics-drilldown-header">Drill-down — services</div>
        <div
          className="flex-row-ali-cen cursor"
          onClick={handleEditColumn}
        >
          <EditOutlinedIcon sx={w20h20BlueIcon} />
          <Typography className="blue-color fs16 font-medium ml5">
            Edit columns ({crntTblHeders?.length})
          </Typography>
        </div>
      </div>

      {/* Search Filters Row */}
      <div className="flex-row mv10">
        <div className="top-row-cen-con w100Per">
          <div className="w15Per">
            <CustomTextField
              options={getServiceNameList()}
              value={searchValues?.serviceName ?? []}
              name="serviceName"
              handleChange={onSearchChange}
              label="Service Name"
              backgroundColor={AppColors.lightBlue2}
              suggetions
              fullWidth
            />
          </div>
          <div
            className="flex-row-ali-cen ml20 filter cursor"
            onClick={() => onFilterChange("visible", true)}
          >
            <TuneIcon sx={{ color: AppColors.appPrimary }} />
            <Typography className="font-medium fs12 blue-color ml10">
              {filterValues?.selectedList?.length > 0 ? `${filterValues?.selectedList?.length} ` : ""}Filter
            </Typography>
          </div>
          <div className="ml20">
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
        </div>
      </div>

      <Typography className="font-medium fs12 gray28 ml30">
        Showing {getTableData()?.length ?? 0} of {serviceTable?.totalRecords ?? 0} rows for the selected filters
      </Typography>

      <div className="mtMin10">
        <ReportTable
          datas={getTableData()}
          columns={crntTblHeders}
          maxHeight="none"
        />
        {Math.ceil((serviceTable?.totalRecords ?? 0) / rowsPerPage) > 1 ? (
          <div className="flex1-end mt20 mb30">
            <Pagination
              count={totalPage}
              variant="outlined"
              color="primary"
              page={page}
              onChange={handleChangePage}
            />
          </div>
        ) : null}
      </div>

      {/* Edit Columns Modal */}
      <CustomModal open={editColModVsble} onClose={onEditModClose} header="Columns" modal modalWidth={25}>
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <CustomButton text="Save" smallBtn onClick={handleSaveTableColumn} />
          </div>
        </div>
      </CustomModal>

      {/* Filter Modal */}
      <CustomModal open={filterValues?.visible} onClose={onCloseFilter} header="Filter" rightModal modalWidth={30}>
        <div className="scroll-80vh ph20">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography className="gray38 fs12 txt-regular">Filter by:</Typography>
              {!filterValues?.added ? (
                filterByList?.map((li, i) => (
                  <div className="flex-row-ali-cen" key={`${i}_li`}>
                    <CustomCheckbox
                      checked={filterValues?.selectedList?.includes(li)}
                      onChange={() => onSelectFilter(li)}
                    />
                    <Typography className="txt-regular fs14 black mb5 mlMin15">{li}</Typography>
                  </div>
                ))
              ) : (
                <div>
                  <div className="flex-row-ali-cen gray-back4 ph10 pv10 br10 width-fit-con mt10 cursor" onClick={() => onFilterAdd(false)}>
                    <AddIcon sx={{ color: AppColors.appPrimary, fontSize: 15 }} />
                    <Typography className="font-medium fs12 blue-color ml5">Add Filter</Typography>
                  </div>
                  {filterValues?.selectedList?.map((li) => (
                    <div className="mt20" key={li}>
                      {dropFields?.includes(li) ? (
                        <CustomSelect
                          list={getDropListByType(li)}
                          value={filterValues?.values?.[li] ?? []}
                          name={li}
                          handleChange={onFilterValuesChange}
                          label={li}
                          multiSelectSearchCheck
                        />
                      ) : (
                        <CustomTextField
                          placeholder={li}
                          fullWidth
                          name={li}
                          handleChange={onFilterValuesChange}
                          value={filterValues?.values?.[li] ?? ""}
                          label={li}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!filterValues?.added ? (
                <div className="flex1-end mt15">
                  <CustomButton text="Add" smallBtn onClick={() => onFilterAdd(true)} />
                </div>
              ) : (
                <div className="flex-row-center mt15">
                  <CustomButton text="Reset All" smallBtn onClick={onFilterReset} tabSelectdBtn />
                  <div className="ml20">
                    <CustomButton text="Apply Filters" smallBtn onClick={onApplyFilter} />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </CustomModal>
    </div>
  );
};

export default ServiceTableReport;
